/**
 * Pure helpers and shared constants for the itinerary list panel.
 *
 * @remarks Colocated with `itineraryList/` components; not part of global `src/tools/`.
 */
import axios from "axios";
import { albums, heart, ticket } from "ionicons/icons";
import { TransportMode } from "../../../services/ItineraryService";
import { typeToOrder } from "../../../tools/Itinerary";
import { type SavedListFilterTabId } from "../../../tools/savedItineraryDisplay.tools";
import { ItineraryType, LegType, type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";

/** Lifecycle status for a per-itinerary offer fetch. */
export type OfferFetchStatus = "idle" | "loading" | "success" | "error";

/** Direction of a transit results page relative to the currently displayed items. */
export type TransitPageDirection = "earlier" | "later";

/**
 * Outcome of one transit page load.
 */
export interface TransitPageResult {

    /** New transit itineraries to merge into the displayed list. */
    "items": ItineraryDTO[];

    /** `true` when the upstream search returned an empty page (schedule exhausted). */
    "exhausted": boolean;

}

/**
 * Offset subtracted from the first displayed transit arrival to anchor an "earlier" page.
 *
 * @remarks The page request uses `isDepartureTimestamp: false` (arrive-by), so MOTIS returns
 * the latest journeys arriving before `firstTransitEnd - offset`. The 1-minute offset
 * excludes the anchor journey itself.
 */
export const TRANSIT_PAGE_EARLIER_ARRIVE_BY_OFFSET_MS = 60 * 1000;

/**
 * Maximum arrive-by requests issued per "earlier" click.
 *
 * @remarks One page can be dominated by later departures arriving before the bound
 * (they are filtered out), so the search chains deeper until enough earlier departures
 * are collected or the schedule is exhausted. This cap bounds the work per click.
 */
export const TRANSIT_PAGE_EARLIER_MAX_REQUESTS = 4;

/**
 * Number of earlier departures to collect before stopping an "earlier" chain early.
 *
 * @remarks Matches the typical MOTIS page size (~5 itineraries) so a click usually
 * prepends a full page of earlier departures.
 */
export const TRANSIT_PAGE_EARLIER_TARGET_COUNT = 5;

/** One filter-tab definition for the saved itinerary list. */
export interface SavedListFilterTabDefinition {
    "id":    SavedListFilterTabId;
    "label": string;

    /** IonIcon rendered before the label (MiniModal tab pattern). */
    "icon": string;

    /** Accent color applied to the icon in every selection state. */
    "iconColor": string;
}

/** Filter tabs shown above the saved-itinerary list. */
export const SAVED_LIST_FILTER_TABS: readonly SavedListFilterTabDefinition[] = [
    {
        "id":        "all",
        "label":     "Tous",
        "icon":      albums,
        "iconColor": "#0284C7",
    },
    {
        "id":        "itineraries",
        "label":     "Favoris",
        "icon":      heart,
        "iconColor": "#E11D48",
    },
    {
        "id":        "tickets",
        "label":     "Billets",
        "icon":      ticket,
        "iconColor": "#11D39C",
    },
];

/**
 * Whether an unknown error is an aborted / cancelled HTTP request.
 *
 * @param error Unknown catch value.
 * @returns `true` for DOM AbortError or Axios cancel errors.
 */
export const isAbortError = ( error: unknown ): boolean => {
    if ( error instanceof DOMException && error.name === "AbortError" ) {
        return true;
    }

    return axios.isCancel( error );
};

/**
 * Whether a carpool itinerary is still incomplete and must not be selectable.
 *
 * @param itinerary Itinerary candidate from search/completion.
 * @returns `true` when `finalized` is explicitly `false`.
 */
export const isCarpoolIncomplete = ( itinerary: ItineraryDTO ): boolean => {
    return itinerary.finalized === false;
};

/**
 * Sorts itineraries by stable type order (mutates the input array).
 *
 * @param itineraries Itineraries to sort in place.
 * @returns The same array reference after sorting.
 */
export const sortItineraries = ( itineraries: ItineraryDTO[] ): ItineraryDTO[] => {
    return itineraries.sort( ( a, b ) => typeToOrder( a.type ?? ItineraryType.MOTIS ) - typeToOrder( b.type ?? ItineraryType.MOTIS ) );
};

/**
 * Converts an id-keyed itinerary map into a type-sorted array.
 *
 * @param itinerariesById Map of itineraries keyed by id, or null/undefined.
 * @returns Sorted itineraries, or an empty array when the map is missing.
 */
export const toSortedArray = ( itinerariesById: Record<string, ItineraryDTO> | null | undefined ): ItineraryDTO[] => {
    if ( itinerariesById === null || itinerariesById === undefined ) {
        return [];
    }

    return sortItineraries( Object.values( itinerariesById ) );
};

/**
 * Merges a patch map into the current itinerary list, then re-sorts by type.
 *
 * @param currentItineraries Current displayed/search itineraries.
 * @param patchById Partial itinerary replacements keyed by id.
 * @returns Updated list (or the original reference when the patch is empty/missing).
 */
export const mergeItineraryPatchById = (
    currentItineraries: ItineraryDTO[],
    patchById: Record<string, ItineraryDTO> | null | undefined,
): ItineraryDTO[] => {
    if ( patchById === null || patchById === undefined ) {
        return currentItineraries;
    }

    const patchEntries = Object.entries( patchById );
    if ( patchEntries.length === 0 ) {
        return currentItineraries;
    }

    const nextItineraries = currentItineraries.map( ( itinerary ) => {
        const patchedItinerary = patchById[itinerary.id];

        return patchedItinerary ?? itinerary;
    } );

    return sortItineraries( nextItineraries );
};

/**
 * Removes pricing segments, evaluation, offer, and leg-level pricing fields from an itinerary.
 *
 * @param itinerary Source itinerary snapshot.
 * @returns A new itinerary without pricing/offer enrichment.
 */
export const stripItineraryPricingAndOfferData = ( itinerary: ItineraryDTO ): ItineraryDTO => {
    const strippedItinerary: ItineraryDTO = {
        ...itinerary,
        "legs": itinerary.legs.map( ( leg ) => {
            const nextLeg = { ...leg };
            delete nextLeg.price;
            delete nextLeg.priceEvaluationSegmentId;

            return nextLeg;
        } ),
    };

    delete strippedItinerary.price;
    delete strippedItinerary.pricingSegments;
    delete strippedItinerary.pricingEvaluation;
    delete strippedItinerary.offer;

    return strippedItinerary;
};

/**
 * Removes only the sales offer projection from an itinerary.
 *
 * @param itinerary Source itinerary snapshot.
 * @returns A new itinerary without `offer`.
 */
export const stripItineraryOfferData = ( itinerary: ItineraryDTO ): ItineraryDTO => {
    const strippedItinerary: ItineraryDTO = { ...itinerary };
    delete strippedItinerary.offer;

    return strippedItinerary;
};

/**
 * Replaces one itinerary inside a list by id.
 *
 * @param currentItineraries Current itinerary list.
 * @param nextItinerary Replacement itinerary.
 * @returns Updated list when a matching id exists; otherwise a shallow copy of the original list.
 */
export const replaceItineraryInList = (
    currentItineraries: readonly ItineraryDTO[],
    nextItinerary: ItineraryDTO,
): ItineraryDTO[] => {
    const hasMatch = currentItineraries.some( ( itinerary ) => itinerary.id === nextItinerary.id );
    if ( !hasMatch ) {
        return [ ...currentItineraries ];
    }

    return currentItineraries.map( ( itinerary ) => {
        return itinerary.id === nextItinerary.id ? nextItinerary : itinerary;
    } );
};

/**
 * Builds transport modes for itinerary search from user mobility preferences.
 *
 * @param hasPersonalBike Whether personal bike routing is enabled.
 * @param hasRentalTypes Whether at least one rental vehicle type is enabled.
 * @param isCarpoolingEnabled Whether carpool partner search is enabled.
 * @returns Transport modes sent in itinerary search requests.
 */
export const buildSearchModes = (
    hasPersonalBike: boolean,
    hasRentalTypes: boolean,
    isCarpoolingEnabled: boolean,
): TransportMode[] => {
    const baseModes: TransportMode[] = [
        TransportMode.FOOT,
        TransportMode.BIKE,
        ...isCarpoolingEnabled ? [ TransportMode.CARPOOL ] : [],
        TransportMode.TRANSIT,
    ];
    const filteredBaseModes = hasPersonalBike
        ? baseModes
        : baseModes.filter( ( mode ) => mode !== TransportMode.BIKE );

    if ( hasRentalTypes !== true ) {
        return filteredBaseModes;
    }

    return filteredBaseModes.includes( TransportMode.RENTAL )
        ? filteredBaseModes
        : [ ...filteredBaseModes, TransportMode.RENTAL ] as TransportMode[];
};

/**
 * Whether a MYCAR itinerary has the fields required to show the car proposition.
 *
 * @param itinerary Candidate MYCAR itinerary from search.
 * @returns `true` when duration and start/end times are defined.
 */
export const isCompleteMyCarItinerary = ( itinerary: ItineraryDTO ): boolean => {
    return itinerary.duration !== undefined && itinerary.startTime !== undefined && itinerary.endTime !== undefined;
};

/**
 * Narrows a bike speed preference to the backend-supported discrete values.
 *
 * @param speed Raw speed from bike prefs.
 * @returns `true` when speed is 15, 25, or 45 km/h.
 */
export const isValidBikeSpeed = ( speed: number ): speed is 15 | 25 | 45 => {
    return speed === 15 || speed === 25 || speed === 45;
};

/**
 * Whether an itinerary contains at least one public-transport leg.
 *
 * @remarks `ItineraryType.MOTIS` also covers direct bike/rental routes, so the transit
 * test must inspect legs rather than the itinerary type.
 *
 * @param itinerary Itinerary candidate.
 * @returns `true` when the itinerary rides public transport.
 */
export const isTransitItinerary = ( itinerary: ItineraryDTO ): boolean => {
    return itinerary.legs.some( ( leg ) => leg.type === LegType.publicTransport );
};

/**
 * Resolves the epoch-ms anchor used to page transit results.
 *
 * @remarks The anchor is the `startTime` of the first (`earlier`) or last (`later`)
 * displayed transit itinerary; non-transit items never contribute the reference time.
 *
 * @param itineraries Currently displayed itineraries.
 * @param direction Page direction relative to the displayed list.
 * @returns Anchor timestamp in epoch ms, or `null` when no usable transit item exists.
 */
export const getTransitPageAnchorTimeMs = (
    itineraries: readonly ItineraryDTO[],
    direction: TransitPageDirection,
): number | null => {
    const transitItineraries = itineraries.filter( isTransitItinerary );
    if ( transitItineraries.length === 0 ) {
        return null;
    }
    const anchor = direction === "earlier"
        ? transitItineraries[0]
        : transitItineraries[transitItineraries.length - 1];
    const anchorMs = Date.parse( anchor.startTime );

    return Number.isFinite( anchorMs ) ? anchorMs : null;
};

/**
 * Resolves the epoch-ms arrive-by bound used to page transit results "earlier".
 *
 * @remarks The bound is `endTime` of the first displayed transit itinerary minus
 * {@link TRANSIT_PAGE_EARLIER_ARRIVE_BY_OFFSET_MS}: earlier-departing journeys that arrive
 * during the first journey's travel window are still returned, while later departures
 * are dropped client-side by `selectNewTransitPageItineraries`.
 *
 * @param itineraries Currently displayed itineraries.
 * @returns Arrive-by bound in epoch ms, or `null` when no usable transit item exists.
 */
export const getEarlierTransitPageArriveByMs = ( itineraries: readonly ItineraryDTO[] ): number | null => {
    const firstTransit = itineraries.find( isTransitItinerary );
    if ( firstTransit === undefined ) {
        return null;
    }
    const endMs = Date.parse( firstTransit.endTime );

    return Number.isFinite( endMs ) ? endMs - TRANSIT_PAGE_EARLIER_ARRIVE_BY_OFFSET_MS : null;
};

/**
 * Selects transit itineraries from a page response that are not already displayed.
 *
 * @remarks Dedupes by deterministic itinerary id against both the displayed list and the
 * page itself. For `earlier` pages, only items departing strictly before the anchor are
 * kept — arrival time is intentionally not constrained.
 *
 * @param currentItineraries Currently displayed itineraries.
 * @param pageItineraries Itineraries returned by the page search.
 * @param direction Page direction relative to the displayed list.
 * @param anchorOverrideMs Optional fixed anchor; used by the "earlier" chain to keep the
 * original first-departure anchor while deduping against items collected in previous rounds.
 * @returns New transit itineraries to merge.
 */
export const selectNewTransitPageItineraries = (
    currentItineraries: readonly ItineraryDTO[],
    pageItineraries: readonly ItineraryDTO[],
    direction: TransitPageDirection,
    anchorOverrideMs: number | null = null,
): ItineraryDTO[] => {
    const anchorMs = anchorOverrideMs ?? getTransitPageAnchorTimeMs( currentItineraries, direction );
    const knownIds = new Set( currentItineraries.map( ( itinerary ) => itinerary.id ) );
    const seenPageIds = new Set<string>();

    return pageItineraries.filter( ( itinerary ) => {
        if ( !isTransitItinerary( itinerary ) ) {
            return false;
        }
        if ( knownIds.has( itinerary.id ) || seenPageIds.has( itinerary.id ) ) {
            return false;
        }
        if ( direction === "earlier" && anchorMs !== null && Date.parse( itinerary.startTime ) >= anchorMs ) {
            return false;
        }
        seenPageIds.add( itinerary.id );

        return true;
    } );
};

/**
 * Merges a transit page into the displayed list.
 *
 * @remarks New items are sorted by `startTime` and appended (`later`) at the very end of
 * the list or prepended (`earlier`) at the very top, so paged results never land in the
 * middle of the list.
 *
 * @param currentItineraries Currently displayed itineraries.
 * @param newTransitItineraries New transit itineraries (already deduped).
 * @param direction Page direction relative to the displayed list.
 * @returns Updated list, or a shallow copy when the page is empty.
 */
export const mergeTransitPageItineraries = (
    currentItineraries: readonly ItineraryDTO[],
    newTransitItineraries: readonly ItineraryDTO[],
    direction: TransitPageDirection,
): ItineraryDTO[] => {
    if ( newTransitItineraries.length === 0 ) {
        return [ ...currentItineraries ];
    }
    const sortedNew = [ ...newTransitItineraries ].sort( ( a, b ) => Date.parse( a.startTime ) - Date.parse( b.startTime ) );

    return direction === "earlier"
        ? [ ...sortedNew, ...currentItineraries ]
        : [ ...currentItineraries, ...sortedNew ];
};

/**
 * Builds the id-keyed itinerary record expected by `evaluateItinerariesPricing`.
 *
 * @param itineraries Itineraries to index.
 * @returns Record keyed by itinerary id.
 */
export const toItineraryRecordById = ( itineraries: readonly ItineraryDTO[] ): Record<string, ItineraryDTO> => {
    return Object.fromEntries( itineraries.map( ( itinerary ) => [ itinerary.id, itinerary ] ) );
};
